import React, { useEffect, useState } from "react";
import { environment } from "environment/environment";
import "./Subscribe.css";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../common/AuthContext";
import { SubscribeModel } from "./SubscribeModel";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Subscribe = () => {
  // const { user, logout, isLoading } = useAuth();
  const {
    user,
    userPlan,
    setUserPlan,
    refreshUserPlan,
    isLoading,
    isPlanLoading,
  } = useAuth();
  const navigate = useNavigate();

  const [allPlanDetails, setAllPlanDetails] = useState([]);
  const [subscribingPlanId, setSubscribingPlanId] = useState(null);
  // ✅ Redirect to login if not logged in
  useEffect(() => {
    if (!isLoading && !user) {
      navigate("/login");
    }
  }, [user, isLoading, navigate]);

  // Re-check when this page is opened or the browser tab becomes active. This
  // keeps the card in sync if the subscription was completed in another tab
  // or if the page was left open while the backend was updated.
  useEffect(() => {
    if (!user?.userId) return undefined;

    const refresh = () => refreshUserPlan(user.userId);
    refresh();

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") refresh();
    };

    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [user?.userId, refreshUserPlan]);

  // ✅ Fetch user plan & all plan details
  useEffect(() => {
  
    const fetchAllPlanDetails = async () => {
      try {
        const response = await fetch(
          `${environment.baseUrl}/plan-detail/get-all-plan-detail`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
          }
        );

        const res = await response.json();

        if (res.status && res.data) {
          setAllPlanDetails(res.data);
          console.log("Fetched allPlanDetails => ", res.data);
        }
      } catch (error) {
        console.error("Error fetching all plan details:", error);
        toast.error("Error fetching plan details!");
      }
    };


    fetchAllPlanDetails();
  }, [user]);

  // ✅ Subscribe click handler
  const handleSubscribe = async (plan) => {
    if (!user?.userId) {
      toast.warn("Please login to subscribe.");
      navigate("/login");
      return;
    }
    if (isPlanLoading) {
      toast.info("Checking your subscription status. Please try again in a moment.");
      return;
    }
    if (userPlan) {
      toast.info("Your current subscription is still active.");
      return;
    }
    // 🔥 ADD: lock only this plan
    setSubscribingPlanId(plan.id);

    // Create a SubscribeModel instance
    const subscribeData = new SubscribeModel(
      0, // id (new subscription)
      user.userId, // userId
      plan.plan, // planName
      plan.id, // planDetailId
      plan.planValidity // planValidity
    );

    console.log("Captured SubscribeModel =>", subscribeData);

    try {
      const response = await fetch(`${environment.baseUrl}/user-pan/save-user-plan`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(subscribeData),
      });

      const res = await response.json();

      if (res.status && res.data) {
        setUserPlan(res.data); // Update user plan in context
        toast.success(res.message, {
          autoClose: 1500,
          onClose: () => navigate("/nse-option-chain"),
        });
        

        console.log("Subscription response:", res.data);

      } else {
        setSubscribingPlanId(null);
        toast.error(res.message || "Subscription failed. Try again.");
      }
    } catch (error) {
      setSubscribingPlanId(null);
      console.error("Error subscribing:", error);
      toast.error("Error occurred while subscribing.");
    }
  };

  // const handleLogout = () => {
  //   logout();
  //   navigate("/login");
  // };

  if (isLoading) return <div className="loading">Loading...</div>;
  if (!user) return null;

  return (
    <main className="subscribe-container">
      <div className="subscribe-page-shell">
        <section className="welcome-banner fade-in" aria-labelledby="membership-page-title">
          <div className="welcome-content">
            <span className="welcome-kicker">
              {userPlan ? "Your access is active" : "Choose your membership"}
            </span>
            <h1 className="welcome-title" id="membership-page-title">
              {userPlan
                ? `You’re all set, ${user?.userName?.split(" ")[0] || "there"}.`
                : `Welcome back, ${user?.userName?.split(" ")[0] || "there"}.`}
            </h1>
            <p className="welcome-text">
              {userPlan
                ? "Your market tools and learning resources are ready whenever you are."
                : "Restore full access with one simple plan. No automatic renewal—renew only when you choose."}
            </p>
          </div>
          <div className={`hero-access-card ${userPlan ? "is-active" : "is-expired"}`}>
            <span className="hero-access-label">
              <i aria-hidden="true" /> Current access
            </span>
            <strong>
              {isPlanLoading
                ? "Checking membership"
                : userPlan
                  ? "Active membership"
                  : "Membership expired"}
            </strong>
            <small>
              {isPlanLoading
                ? "Confirming with the server…"
                : userPlan
                  ? `${userPlan.daysLeft ?? "--"} days remaining`
                  : "Select a plan below to continue"}
            </small>
          </div>
        </section>

      <div className="info-card-top">
        <section className="card account-summary-card">
          <div className="card-heading">
            <span className="card-heading-icon account-icon" aria-hidden="true">
              {(user?.userName || "U").charAt(0).toUpperCase()}
            </span>
            <div><small>Account</small><h2>User details</h2></div>
          </div>
          <div className="detail-row">
            <span className="label">User ID:</span>
            <span className="value">{user?.userId || "--"}</span>
          </div>
          <div className="detail-row">
            <span className="label">Name:</span>
            <span className="value">{user?.userName || "--"}</span>
          </div>
          <div className="detail-row">
            <span className="label">Mobile:</span>
            <span className="value">{user?.mobileNumber || "--"}</span>
          </div>
          <div className="detail-row">
            <span className="label">Email:</span>
            <span className="value">{user?.email || "--"}</span>
          </div>
          <div className="detail-row">
            <span className="label">Role:</span>
            <span className="value">{user?.admin ? "Admin" : "Member"}</span>
          </div>
        </section>

        <section className={`card membership-summary-card ${userPlan ? "is-active" : "is-expired"}`}>
          <div className="card-heading card-heading-split">
            <div><small>Membership</small><h2>Plan & validity</h2></div>
            <div className={`subscription-status ${userPlan ? "subscription-status-active" : "subscription-status-expired"}`}>
              <i aria-hidden="true" />
              {isPlanLoading ? "Checking" : userPlan ? "Active" : "Expired"}
            </div>
          </div>
          {!isPlanLoading && !userPlan && (
            <div className="subscription-empty-message">
              <span aria-hidden="true">!</span>
              <p><strong>Your paid access has ended</strong>Choose a plan below to unlock all premium pages again.</p>
            </div>
          )}
          <div className="detail-row">
            <span className="label">Plan:</span>
            <span className="value">
              {userPlan?.planName || "No active plan"}
            </span>
          </div>
          <div className="detail-row">
            <span className="label">Amount:</span>
            <span className="value">{userPlan ? `₹${userPlan.planAmount}` : "--"}</span>
          </div>
          <div className="detail-row">
            <span className="label">Plan Validity</span>
            <span className="value">
              {userPlan?.planValidity ?? "--"} days
            </span>
          </div>
          <div className="detail-row">
            <span className="label">Days Left:</span>
            <span className="value">
              {userPlan?.daysLeft ?? "--"}
            </span>
          </div>
          <div className="detail-row">
            <span className="label">Start:</span>
            <span className="value">
              {userPlan?.subscribeDateStart || "--"}
            </span>
          </div>
          <div className="detail-row">
            <span className="label">End:</span>
            <span className="value">
              {userPlan?.subscribeDateEnd || "--"}
            </span>
          </div>
        </section>

        <section className="card performance-summary-card">
          <div className="card-heading">
            <span className="card-heading-icon performance-icon" aria-hidden="true">↗</span>
            <div><small>Snapshot</small><h2>Performance</h2></div>
          </div>
          <div className="detail-row">
            <span className="label">Total Trades:</span>
            <span className="value">248</span>
          </div>
          <div className="detail-row">
            <span className="label">Win Rate:</span>
            <span className="value">78%</span>
          </div>
          <div className="detail-row">
            <span className="label">Profit:</span>
            <span className="value">₹1,24,500</span>
          </div>
        </section>
      </div>

      <section className="plans-section" id="available-plans" aria-labelledby="plans-title">
        <div className="plans-section-heading">
          <div>
            <span className="plans-kicker">Simple pricing</span>
            <h2 id="plans-title">Choose the access period that fits you</h2>
            <p>Every plan includes the complete learning and market-data toolkit.</p>
          </div>
          <div className="plan-assurances" aria-label="Plan assurances">
            <span><i aria-hidden="true">✓</i> Full access</span>
            <span><i aria-hidden="true">✓</i> No auto-renewal</span>
          </div>
        </div>

        <div className="info-card-bottom">
          {allPlanDetails && allPlanDetails.length > 0 ? (
            allPlanDetails.map((plan) => {
              const isLoadingThisPlan = subscribingPlanId === plan.id;
              const isCurrentPlan = userPlan?.planDetailId === plan.id;
              const isPopular = plan.planValidity === 90;
              const isBestValue = plan.planValidity >= 365;

              return (
                <article
                  key={plan.id}
                  className={`card plan-card ${isPopular ? "is-popular" : ""} ${isCurrentPlan ? "is-current" : ""}`}
                >
                  {isPopular && <span className="plan-badge">Most popular</span>}
                  {isBestValue && <span className="plan-badge plan-badge-value">Best value</span>}

                  <div className="plan-header">
                    <span className="plan-duration">{plan.planValidity} days</span>
                    <h3>{plan.plan ?? "Membership plan"}</h3>
                    <div className="plan-price">
                      <strong>₹{plan.amount ?? "N/A"}</strong>
                      <small>one-time payment</small>
                    </div>
                  </div>

                  <div className="plan-divider" />
                  <p className="plan-includes">Everything you need:</p>
                  <ul className="plan-feature-list">
                    <li><span aria-hidden="true">✓</span> Live option chain</li>
                    <li><span aria-hidden="true">✓</span> Historical market data</li>
                    <li><span aria-hidden="true">✓</span> Sunday live class</li>
                    <li><span aria-hidden="true">✓</span> Recorded class library</li>
                    <li className="plan-validity-feature">
                      <span aria-hidden="true">✓</span> {plan.planValidity} days uninterrupted access
                    </li>
                  </ul>

                  <div className="button-container">
                    <button
                      className="subscribe-button"
                      type="button"
                      disabled={isLoadingThisPlan || isPlanLoading || Boolean(userPlan)}
                      onClick={() => handleSubscribe(plan)}
                    >
                      {isLoadingThisPlan ? (
                        <><span className="spinner-border spinner-border-sm me-2" />Activating plan…</>
                      ) : isCurrentPlan ? (
                        "Your current plan"
                      ) : isPlanLoading ? (
                        "Checking membership…"
                      ) : userPlan ? (
                        "Available after expiry"
                      ) : (
                        "Choose this plan"
                      )}
                    </button>
                  </div>
                </article>
              );
            })
          ) : (
            <div className="plans-empty">Loading available plans…</div>
          )}
        </div>

        <p className="plans-footnote">
          Access starts immediately after activation and ends automatically on the displayed expiry date.
        </p>
      </section>

      {/* ✅ Toast container for notifications */}
      <ToastContainer position="top-center" autoClose={3000} hideProgressBar={false} />
      </div>
    </main>
  );
};

export default Subscribe;
